import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from 'services/firebase';
import { signInWithGoogle, signOutUser } from './authService';

const AuthContext = createContext(null);

// Provides the signed-in Firebase user and their Firestore profile to the app.
// The profile doc (users/{uid}) is created server-side by the createUserProfile
// Cloud Function; we subscribe to it live, so it appears automatically the moment
// the function finishes writing it after a first sign-in.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  // The uid whose admins/{uid} lookup has actually come back. Lets us tell
  // "confirmed not an admin" apart from "the admin check hasn't resolved yet".
  const [adminCheckedUid, setAdminCheckedUid] = useState(null);

  // Track the auth session.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  // While signed in, subscribe to the user's profile document.
  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoading(false);
      return undefined;
    }
    setProfileLoading(true);
    const ref = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        setProfile(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setProfileLoading(false);
      },
      () => setProfileLoading(false)
    );
    return unsubscribe;
  }, [user]);

  // Admin = the signed-in user's uid has a doc in /admins (Lafrun staff). Used to
  // reveal the /admin dashboard. Reads only the caller's own admins/{uid} doc.
  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setAdminCheckedUid(null);
      return undefined;
    }
    const ref = doc(db, 'admins', user.uid);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => { setIsAdmin(snap.exists()); setAdminCheckedUid(user.uid); },
      () => { setIsAdmin(false); setAdminCheckedUid(user.uid); }
    );
    return unsubscribe;
  }, [user]);

  // True while signed in but the admins/{uid} lookup for THIS user has not come
  // back yet. The admin gate must wait on this rather than read isAdmin === false
  // as "not an admin", otherwise a real admin is shown the "Not authorized" screen
  // on every load for the length of the Firestore round-trip. Derived (not its own
  // state) so a newly signed-in user reads as loading on the very first render,
  // before the effect above re-runs.
  const adminLoading = !!user && adminCheckedUid !== user.uid;

  const value = {
    user,
    profile,
    authLoading,
    profileLoading,
    isAdmin,
    adminLoading,
    signIn: signInWithGoogle,
    signOut: signOutUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
